/**
 * Turns a parsed chat + a date range into (a) the list of media to attach and
 * (b) the transcript document the agent reads.
 *
 * Pure — no DOM, no crypto. Hashing happens in the component and is passed in,
 * which keeps this file runnable under plain Node for the parser check.
 */

import type { ChatMessage, MediaKind, ParsedChat } from "./chat";

/** Upload ceiling per photo. The backend rejects above 25 MB; stay clear of it. */
export const MAX_PHOTO_BYTES = 20 * 1024 * 1024;
/**
 * Hard cap on photos per send. Each one is a compress + upload round trip on a
 * phone, so an unbounded slice could sit there for minutes. Anything dropped is
 * reported in the UI and written into the transcript — never silently.
 */
export const MAX_PHOTOS = 25;

export interface SliceOptions {
  includePhotos: boolean;
  includeVideos: boolean;
}

export interface MediaRef {
  message: ChatMessage;
  filename: string;
  kind: MediaKind;
  /** Byte size from the archive index, when the file was found. */
  sizeBytes: number | null;
  /** False when the chat references a file the archive doesn't contain. */
  presentInArchive: boolean;
}

export interface ChatSlice {
  messages: ChatMessage[];
  from: Date;
  to: Date;
  /** Photos that will actually be uploaded, after cap and size filtering. */
  photosToAttach: MediaRef[];
  /** Photos inside the range that were left out, with the reason. */
  photosSkipped: Array<MediaRef & { reason: string }>;
  /** Videos, always reference-only — never uploaded. */
  videos: MediaRef[];
  /** Voice notes, reference-only and never transcribed. */
  voiceNotes: MediaRef[];
  otherMedia: MediaRef[];
  totalMessagesInChat: number;
}

const fmt2 = (n: number) => String(n).padStart(2, "0");

/** `2026-08-04 09:12:41` in local wall-clock, matching how the export reads. */
export function stamp(d: Date): string {
  return (
    `${d.getFullYear()}-${fmt2(d.getMonth() + 1)}-${fmt2(d.getDate())} ` +
    `${fmt2(d.getHours())}:${fmt2(d.getMinutes())}:${fmt2(d.getSeconds())}`
  );
}

export function humanBytes(n: number | null): string {
  if (n === null) return "unknown size";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * The name a photo will carry once uploaded. The composer's existing image path
 * re-encodes to JPEG and swaps the extension, so a `.heic` from an iPhone lands
 * as `.jpg`. The transcript quotes this name so the agent can match the file it
 * was handed to the message it belongs to.
 */
export function attachedAs(filename: string): string {
  return filename.replace(/\.[^.]+$/, "") + ".jpg";
}

export function sliceChat(
  parsed: ParsedChat,
  from: Date,
  to: Date,
  opts: SliceOptions,
  sizeOf: (filename: string) => number | null,
): ChatSlice {
  const fromMs = from.getTime();
  const toMs = to.getTime();
  const messages = parsed.messages.filter((m) => {
    const t = m.at.getTime();
    return t >= fromMs && t <= toMs;
  });

  const refFor = (m: ChatMessage): MediaRef | null => {
    if (!m.media?.filename) return null;
    const size = sizeOf(m.media.filename);
    return {
      message: m,
      filename: m.media.filename,
      kind: m.media.kind,
      sizeBytes: size,
      presentInArchive: size !== null,
    };
  };

  const allRefs = messages.map(refFor).filter((r): r is MediaRef => r !== null);

  const photosAll = allRefs.filter((r) => r.kind === "image" || r.kind === "sticker");
  const videos = allRefs.filter((r) => r.kind === "video");
  const voiceNotes = allRefs.filter((r) => r.kind === "audio");
  const otherMedia = allRefs.filter(
    (r) => r.kind === "document" || r.kind === "unknown",
  );

  const photosToAttach: MediaRef[] = [];
  const photosSkipped: Array<MediaRef & { reason: string }> = [];
  if (!opts.includePhotos) {
    for (const p of photosAll) photosSkipped.push({ ...p, reason: "photos switched off" });
  } else {
    for (const p of photosAll) {
      if (!p.presentInArchive) {
        photosSkipped.push({ ...p, reason: "not in the archive (exported without media?)" });
      } else if ((p.sizeBytes ?? 0) > MAX_PHOTO_BYTES) {
        photosSkipped.push({ ...p, reason: `too large (${humanBytes(p.sizeBytes)})` });
      } else if (photosToAttach.length >= MAX_PHOTOS) {
        photosSkipped.push({ ...p, reason: `over the ${MAX_PHOTOS}-photo limit for one send` });
      } else {
        photosToAttach.push(p);
      }
    }
  }

  return {
    messages,
    from,
    to,
    photosToAttach,
    photosSkipped,
    videos: opts.includeVideos ? videos : [],
    voiceNotes,
    otherMedia,
    totalMessagesInChat: parsed.messages.length,
  };
}

/** One rendered line per message, verbatim text, with media noted in place. */
export function buildMessageBlock(slice: ChatSlice, opts: SliceOptions): string {
  const attachedNames = new Set(slice.photosToAttach.map((p) => p.filename));
  const out: string[] = [];
  for (const m of slice.messages) {
    const who = m.sender ?? "(system)";
    const head = `[#${m.n} ${stamp(m.at)}] ${who}:`;
    const parts: string[] = [];
    if (m.media?.filename) {
      const f = m.media.filename;
      const size = humanBytes(
        slice.photosToAttach.find((p) => p.filename === f)?.sizeBytes ??
          slice.videos.find((p) => p.filename === f)?.sizeBytes ??
          slice.voiceNotes.find((p) => p.filename === f)?.sizeBytes ??
          slice.otherMedia.find((p) => p.filename === f)?.sizeBytes ??
          null,
      );
      if (attachedNames.has(f)) {
        parts.push(`[photo ATTACHED: ${f} — the attached file is named "${attachedAs(f)}"]`);
      } else if (m.media.kind === "video") {
        parts.push(
          opts.includeVideos
            ? `[video NOT ATTACHED: ${f}, ${size} — videos are never uploaded; ask Julian for it if it matters]`
            : `[video here, not included]`,
        );
      } else if (m.media.kind === "audio") {
        parts.push(`[voice note NOT ATTACHED and NOT transcribed: ${f}, ${size} — ask Julian what was said if it matters]`);
      } else {
        parts.push(`[${m.media.kind} referenced, not attached: ${f}, ${size}]`);
      }
    } else if (m.media) {
      parts.push(`[media was omitted from this export]`);
    }
    if (m.text.trim()) parts.push(m.text.trim());
    out.push(parts.length ? `${head} ${parts.join(" ")}` : head);
  }
  return out.join("\n");
}

export interface DocumentMeta {
  chatLabel: string;
  archiveName: string;
  archiveBytes: number;
  archiveModified: Date | null;
  chatTxtSha256: string;
  messageBlockSha256: string;
  parsed: ParsedChat;
  slice: ChatSlice;
  opts: SliceOptions;
  generatedAt: Date;
}

/**
 * The document handed to the agent. It leads with what it is and what is
 * deliberately missing, because an agent that doesn't know a video existed will
 * confidently act as though it didn't.
 */
export function buildDocument(meta: DocumentMeta, messageBlock: string): string {
  const { slice, parsed, opts } = meta;
  const L: string[] = [];

  L.push(`# WhatsApp messages — ${meta.chatLabel}`);
  L.push("");
  L.push(
    "This is a slice of a WhatsApp chat export that Julian selected by date in the " +
      "Reputifly dashboard. The message text below is verbatim from the export — nothing " +
      "has been summarised, reordered or rewritten.",
  );
  L.push("");

  L.push("## Range");
  L.push("");
  L.push(`- From: ${stamp(slice.from)}`);
  L.push(`- To:   ${stamp(slice.to)}`);
  L.push(
    "- Times are the exporting phone's own clock. A WhatsApp export carries no timezone, " +
      "so these are wall-clock times and have not been converted.",
  );
  if (parsed.dateOrderConfidence === "ambiguous") {
    L.push(
      "- ⚠️ Day/month order could not be proven from this export; day-first was assumed. " +
        "If the dates below look wrong, say so rather than acting on them.",
    );
  } else {
    L.push(
      `- Dates read as ${parsed.dateOrder === "dmy" ? "day/month" : "month/day"} ` +
        `(${parsed.dateOrderConfidence === "proven" ? "proven from the file" : "inferred from message order"}).`,
    );
  }
  L.push("");

  L.push("## What you have, and what you don't");
  L.push("");
  L.push(
    `- ${slice.messages.length.toLocaleString()} messages, out of ${slice.totalMessagesInChat.toLocaleString()} in the whole chat. ` +
      "Every message inside the range is here — the selection is contiguous, nothing was dropped.",
  );
  if (slice.photosToAttach.length > 0) {
    L.push(
      `- ${slice.photosToAttach.length} photo(s) ARE attached to this message. Each one is marked ` +
        "[photo ATTACHED: …] at the point it was sent, with the filename you'll find it under.",
    );
  } else if (opts.includePhotos) {
    L.push("- No photos in this range.");
  } else {
    L.push("- Photos were switched off for this send.");
  }
  if (slice.photosSkipped.length > 0) {
    L.push(`- ${slice.photosSkipped.length} photo(s) in range were NOT sent:`);
    for (const p of slice.photosSkipped.slice(0, 12)) {
      L.push(`    - ${p.filename} — ${p.reason}`);
    }
    if (slice.photosSkipped.length > 12) {
      L.push(`    - …and ${slice.photosSkipped.length - 12} more`);
    }
  }
  if (slice.videos.length > 0) {
    L.push(
      `- ${slice.videos.length} video(s) are referenced but NOT attached. Video is never uploaded, whatever its size. ` +
        "You are not expected to watch them. Use the surrounding messages to work out what the video was for " +
        "and where it belongs; if the content genuinely matters, ask Julian.",
    );
  }
  if (slice.voiceNotes.length > 0) {
    L.push(
      `- ${slice.voiceNotes.length} voice note(s) are referenced but NOT attached and NOT transcribed. ` +
        "We cannot transcribe audio. If one looks important, ask Julian what was said.",
    );
  }
  if (slice.otherMedia.length > 0) {
    L.push(`- ${slice.otherMedia.length} other file(s) referenced but not attached.`);
  }
  L.push("");

  L.push("## How to read a line");
  L.push("");
  L.push("```");
  L.push("[#3664 2026-08-04 09:12:41] Ian Leong: the message text");
  L.push("```");
  L.push("");
  L.push("`#3664` is the message's position in the original export, so you can cite it exactly.");
  L.push("");

  L.push("## Integrity");
  L.push("");
  L.push(`- Archive: ${meta.archiveName} (${humanBytes(meta.archiveBytes)})`);
  if (meta.archiveModified) L.push(`- Archive last modified: ${stamp(meta.archiveModified)}`);
  L.push(`- SHA-256 of the export's chat text: ${meta.chatTxtSha256}`);
  L.push(`- SHA-256 of the message block below: ${meta.messageBlockSha256}`);
  L.push(`- Prepared: ${stamp(meta.generatedAt)}`);
  if (parsed.warnings.length > 0) {
    L.push("");
    for (const w of parsed.warnings) L.push(`- ⚠️ ${w}`);
  }
  L.push("");
  L.push("## Messages");
  L.push("");
  L.push(messageBlock);
  L.push("");
  return L.join("\n");
}
